/**
 * Bulk (Foundry: item/physical/bulk.ts and actor/inventory/bulk.ts). A Bulk value is a number
 * with negligible as 0, light as 0.1 and whole Bulk as integers; arithmetic rounds to one
 * decimal so ten light items make 1 Bulk. Stacks of ammunition, coins and gems weigh by the
 * group (ten arrows are light, a thousand coins are 1 Bulk).
 */
import { SIZES } from "./values";

export interface StackDefinition {
  /** How many items make one unit of the group's bulk. */
  size: number;
  /** The bulk of one unit, in light units (10 light = 1 Bulk). */
  lightBulk: number;
}

/** Foundry's STACK_DEFINITIONS. */
export const STACK_DEFINITIONS: Readonly<Record<string, StackDefinition>> = {
  bolts: { size: 10, lightBulk: 1 },
  arrows: { size: 10, lightBulk: 1 },
  slingBullets: { size: 10, lightBulk: 1 },
  blowgunDarts: { size: 10, lightBulk: 1 },
  woodenTaws: { size: 10, lightBulk: 1 },
  rounds5: { size: 5, lightBulk: 1 },
  rounds10: { size: 10, lightBulk: 1 },
  coins: { size: 1000, lightBulk: 10 },
  gems: { size: 2000, lightBulk: 10 },
  upb: { size: 1000, lightBulk: 10 },
};

export type Size = (typeof SIZES)[number];

export class Bulk {
  /** Negligible 0, light 0.1, whole Bulk as an integer part. */
  readonly value: number;

  constructor(value = 0) {
    this.value = Math.round(Math.max(Number.isFinite(value) ? value : 0, 0) * 10) / 10;
  }

  get normal(): number {
    return Math.floor(this.value);
  }

  get light(): number {
    return Math.round((this.value - this.normal) * 10);
  }

  get isNegligible(): boolean {
    return this.value === 0;
  }

  get isLight(): boolean {
    return this.value > 0 && this.value < 1;
  }

  toLightUnits(): number {
    return this.normal * 10 + this.light;
  }

  /** One step up: negligible to light, light to 1, 1 or more by 1. */
  increment(): Bulk {
    if (this.isNegligible) return new Bulk(0.1);
    if (this.isLight) return new Bulk(1);
    return new Bulk(this.value + 1);
  }

  /** One step down (the printed rule for dawnsilver and duskwood): 1 becomes light, light stays light, 2 or more by 1. */
  decrement(): Bulk {
    if (this.isNegligible || this.isLight) return new Bulk(this.value);
    if (this.normal === 1 && this.light === 0) return new Bulk(0.1);
    return new Bulk(this.value - 1);
  }

  plus(other: number | Bulk): Bulk {
    return new Bulk(this.value + (typeof other === "number" ? other : other.value));
  }

  minus(other: number | Bulk): Bulk {
    return new Bulk(this.value - (typeof other === "number" ? other : other.value));
  }

  times(factor: number): Bulk {
    return new Bulk(Math.round(this.value * factor * 10) / 10);
  }

  /** "—", "L", "2L", "3", "3, 1L" as Foundry's short labels show it. */
  toString(): string {
    const { light, normal } = this;
    if (this.isNegligible) return "—";
    if (this.value === normal) return String(normal);
    if (normal === 0 && light === 1) return "L";
    if (normal === 0) return `${light}L`;
    return `${normal}, ${light}L`;
  }

  double(): Bulk {
    if (this.isNegligible) return new Bulk(0.1);
    if (this.isLight) return new Bulk(1);
    return this.times(2);
  }

  halve(): Bulk {
    if (this.isNegligible || this.isLight) return new Bulk();
    if (this.normal === 1) return new Bulk(0.1);
    return this.times(0.5);
  }

  /**
   * Foundry's `convertToSize`: an item larger than its bearer doubles per size step, one
   * smaller halves per step (1 to light, light to negligible). Small counts as medium.
   */
  convertToSize(itemSize: Size, actorSize: Size): Bulk {
    const sizes = SIZES.filter((s) => s !== "sm");
    const itemIndex = sizes.indexOf(itemSize === "sm" ? "med" : itemSize);
    const actorIndex = sizes.indexOf(actorSize === "sm" ? "med" : actorSize);
    if (itemIndex > actorIndex) {
      let result: Bulk = this;
      for (let i = 0; i < itemIndex - actorIndex; i++) result = result.double();
      return result;
    }
    if (itemIndex < actorIndex) {
      let result: Bulk = this;
      for (let i = 0; i < actorIndex - itemIndex; i++) {
        result = result.value <= 0.1 ? new Bulk() : result.value <= 1 ? new Bulk(0.1) : new Bulk(Math.max(0.1, Math.floor(0.5 * this.value)));
      }
      return result;
    }
    return new Bulk(this.value);
  }
}

export function isSize(value: unknown): value is Size {
  return typeof value === "string" && (SIZES as readonly string[]).includes(value);
}
